import React, { useEffect, useRef, useState } from "react";
import { motion, useScroll, useSpring, useMotionValue } from "framer-motion";

/**
 * ScrollProgress - top-of-page progress bar
 */
export function ScrollProgress() {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 100, damping: 20 });
  return (
    <motion.div
      style={{ scaleX, transformOrigin: "0%" }}
      className="fixed top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#B8E14A] via-[#FFB7B2] to-[#A8E6CF] z-50"
    />
  );
}

/**
 * CursorSpotlight - soft glowing blob that follows the mouse
 */
export function CursorSpotlight() {
  const [pos, setPos] = useState({ x: -400, y: -400 });
  useEffect(() => {
    const move = (e) => setPos({ x: e.clientX, y: e.clientY });
    window.addEventListener("mousemove", move);
    return () => window.removeEventListener("mousemove", move);
  }, []);
  return (
    <div
      className="pointer-events-none fixed inset-0 z-30 mix-blend-multiply hidden md:block"
      style={{
        background: `radial-gradient(400px circle at ${pos.x}px ${pos.y}px, rgba(212,175,55,0.10), transparent 60%)`,
        transition: "background 0.05s linear",
      }}
    />
  );
}

/**
 * Marquee - infinite horizontal scrolling text/items
 */
export function Marquee({ items, className = "" }) {
  return (
    <div className={`overflow-hidden py-8 ${className}`}>
      <div className="flex whitespace-nowrap animate-marquee">
        {[...items, ...items].map((item, i) => (
          <div key={i} className="flex items-center gap-8 mx-8 shrink-0">
            <span className="font-heading text-3xl md:text-7xl text-[#B8E14A]" style={{ WebkitTextStroke: '2px #1F5F5C' }}>{item}</span>
            <span className="w-3 h-3 rounded-full bg-[#B8E14A]" />
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * MagneticButton - button that gently pulls toward the mouse cursor
 */
export function Magnetic({ children, strength = 0.3, className = "" }) {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 200, damping: 15 });
  const sy = useSpring(y, { stiffness: 200, damping: 15 });
  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    x.set((e.clientX - (r.left + r.width / 2)) * strength);
    y.set((e.clientY - (r.top + r.height / 2)) * strength);
  };
  const onLeave = () => { x.set(0); y.set(0); };
  return (
    <motion.div ref={ref} onMouseMove={onMove} onMouseLeave={onLeave}
      style={{ x: sx, y: sy }} className={className}>
      {children}
    </motion.div>
  );
}
